import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  DialogTitle,
  DialogActions,
  Paper,
  Grid,
  Avatar,
  Box,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { UPDATE_CONTAINER_DIALOG } from '../../../../core/constants/dialog';
import Spinner from '../../Spinner';
import { useGlobalCore } from '../../../../core/modules/global';
import { isEmpty } from 'lodash';
import { useCompanyCore } from '../../../../core/modules/company';
import { useContainerCore } from '../../../../core/modules/container';
import { useMasterCore } from '../../../../core/modules/master';
import { useLocationCore } from '../../../../core/modules/location';
import { useWarehouseCore } from '../../../../core/modules/warehouse';
import Swal from 'sweetalert2';
import { useMeCore } from '../../../../core/modules/me';
import { PutContainerPayloadRequest } from '../../../../core/modules/container/interfaces/put-container';
import { DESTINY_CONTAINER_TYPES } from '../../../../core/constants/containerTypes';
import { ContainerCategory } from '../../../../core/interfaces/category.interface';
import CopyButton from '../../shared/CopyButton';
import { usePrintLabel } from '../../../../core/hooks/usePrintLabel';
import PrintIcon from '@mui/icons-material/Print';
import { Container } from '../../../../core/interfaces/container.interface';
import { Autocomplete, IconButton } from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ContainerCategorySelect from '../../shared/ContainerCategorySelect';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { containersApiService } from '../../../../core/modules/container/services/api';

// Interfaz para el modal de éxito
interface ContainerSuccessData {
  id: number;
  code: string;
  description: string;
  container_type_name: string;
  container_category_name: string;
  container_status_name: string;
  location_description: string;
  container_status_id: number;
}

const schema = yup.object().shape({
  code: yup.string().optional(),
  container_status_id: yup.number().required('Campo obligatorio'),
  container_category_id: yup.number().required('Campo obligatorio'),
  container_recipient_type_id: yup.number().required('Campo obligatorio'),
  description: yup.string().optional(),
  price_range_id: yup.number().nullable().notRequired().transform((value, originalValue) => {
    if (originalValue === '' || originalValue === null || originalValue === undefined) {
      return null;
    }
    const numValue = Number(value);
    return isNaN(numValue) ? null : numValue;
  }),
  channel_type_id: yup.number().nullable().notRequired().transform((value, originalValue) => {
    if (originalValue === '' || originalValue === null || originalValue === undefined) {
      return null;
    }
    const numValue = Number(value);
    return isNaN(numValue) ? null : numValue;
  }),
  location_id: yup.number().nullable().transform((value, originalValue) => {
    if (originalValue === '' || originalValue === null || originalValue === undefined) {
      return null;
    }
    return isNaN(value) ? null : Number(value);
  }),
});

const UpdateDialog = () => {
  const { companyId } = useCompanyCore();
  const { user } = useMeCore();
  const { dialogs, closeDialog } = useGlobalCore();
  const { containerTypes, containerCategories, channelTypes, priceRanges, containerRecipientTypes, getTypes } =
    useMasterCore();
  const { locations } = useLocationCore();
  const { warehouses, getWarehouses } = useWarehouseCore();
  const {
    options,
    container,
    getContainers,
    putContainer,
    deleteContainer,
    setContainer,
  } = useContainerCore();
  const { printDestinationContainer } = usePrintLabel();

  const [loading, setLoading] = useState(false);
  const [containerType, setContainerType] = useState<number>();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [updatedContainer, setUpdatedContainer] = useState<ContainerSuccessData | null>(null);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [filesToDelete, setFilesToDelete] = useState<number[]>([]);
  
  // Constante para máximo de imágenes (parametrizable)
  const MAX_CONTAINER_FILES = 10;

  const canWrite = useMemo(
    () =>
      user && (user.privileges.superAdmin || user.privileges.writeContainer),
    [user]
  );

  const { control, formState, handleSubmit, resetField, setValue, trigger } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;


  // Función recursiva para encontrar una categoría en el árbol (incluyendo hijos)
  const findCategoryRecursively = useCallback((categories: ContainerCategory[], categoryId: number): ContainerCategory | null => {
    for (const category of categories) {
      if (category.id === categoryId) {
        return category;
      }
      if (category.child_categories && category.child_categories.length > 0) {
        const found = findCategoryRecursively(category.child_categories as ContainerCategory[], categoryId);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }, []);

  const resetForm = useCallback(() => {
    resetField('code');
    resetField('description');
    resetField('channel_type_id');
    resetField('price_range_id');
    resetField('container_category_id');
    resetField('container_status_id');
    resetField('container_recipient_type_id');
    resetField('location_id');
    setContainer(null);
    closeDialog(UPDATE_CONTAINER_DIALOG);
  }, [closeDialog, resetField, setContainer]);

  useEffect(() => {
    if (companyId && Array.isArray(dialogs) && dialogs.includes(UPDATE_CONTAINER_DIALOG)) {
      getTypes(companyId, 'containerRecipient');
    }
  }, [companyId, dialogs, getTypes]);

  useEffect(() => {
    if (companyId) {
      getWarehouses(companyId);
    }
  }, [companyId, getWarehouses]);

  const locationOptions = useMemo(() => {
    return locations.map(loc => {
      const warehouse = warehouses.find(w => w.id === loc.warehouse_id);
      const warehouseCode = warehouse?.code || 'SIN BODEGA';
      return {
        ...loc,
        label: `${loc.code} - ${warehouseCode} - ${loc.description}`,
        warehouse_code: warehouseCode,
      };
    });
  }, [locations, warehouses]);

  useEffect(() => {
    if (container) {
      setValue('code', container.code);
      setValue('container_status_id', container.container_status_id);
      setValue('container_category_id', container.container_category_id);
      setValue('container_recipient_type_id', container.container_recipient_type_id ?? (containerRecipientTypes.length > 0 ? containerRecipientTypes[0].id : 0));
      setValue('price_range_id', container.price_range_id ?? null);
      setValue('channel_type_id', container.channel_type_id ?? null);
      setValue('description', container.description);
      setValue('location_id', container.location_id || undefined);

      // Buscar la categoría recursivamente (incluyendo hijos)
      const category = findCategoryRecursively(containerCategories as ContainerCategory[], container.container_category_id);
      if (category) {
        setContainerType(category.container_type_id);
      }
      
      // Limpiar archivos al cambiar de contenedor
      setNewFiles([]);
      setFilesToDelete([]);
      
      // Trigger validation después de establecer todos los valores
      setTimeout(() => {
        trigger();
      }, 200);
    }
  }, [setValue, container, containerCategories, findCategoryRecursively, containerRecipientTypes.length, trigger]);

  const onSubmit = useCallback(
    (values: any) => {
      if (companyId && container) {
        setLoading(true);
        // Asegurar que description sea siempre string para la API
        // Manejar valores null/undefined para campos opcionales
        const payload: any = {
          ...values,
          description: values.description || '',
        };
        
        // Solo incluir price_range_id si tiene un valor
        if (values.price_range_id !== null && values.price_range_id !== undefined && values.price_range_id !== '') {
          payload.price_range_id = Number(values.price_range_id);
        } else {
          payload.price_range_id = null;
        }
        
        // Solo incluir channel_type_id si tiene un valor
        if (values.channel_type_id !== null && values.channel_type_id !== undefined && values.channel_type_id !== '') {
          payload.channel_type_id = Number(values.channel_type_id);
        } else {
          payload.channel_type_id = null;
        }
        putContainer(
          companyId,
          container.id,
          payload,
          async () => {
            // Eliminar archivos marcados para eliminar
            if (filesToDelete.length > 0 && companyId) {
              for (const fileId of filesToDelete) {
                try {
                  await containersApiService.deleteContainerFile(companyId, container.id, fileId);
                } catch (error) {
                  console.error('Error al eliminar imagen:', error);
                  // Continuar con las demás imágenes aunque una falle
                }
              }
            }

            // Subir nuevas imágenes
            if (newFiles.length > 0 && companyId) {
              for (const file of newFiles) {
                try {
                  await containersApiService.postContainerFile(companyId, container.id, file);
                } catch (error) {
                  console.error('Error al subir imagen:', error);
                  // Continuar con las demás imágenes aunque una falle
                }
              }
            }

            // Crear el objeto del contenedor actualizado para mostrar en el modal
            const containerData: ContainerSuccessData = {
              id: container.id,
              code: container.code,
              description: values.description || '',
              container_type_name: containerTypes.find(t => t.id === containerType)?.description || containerTypes.find(t => t.id === containerType)?.name || '',
              container_category_name: containerCategories.find(c => c.id === values.container_category_id)?.name || '',
              container_status_name: DESTINY_CONTAINER_TYPES.find(s => s.value === values.container_status_id)?.name || '',
              location_description: (() => {
                const selected = locationOptions.find(l => l.id === values.location_id);
                if (!selected) {
                  return 'Sin ubicación';
                }
                return selected.label;
              })(),
              container_status_id: values.container_status_id,
            };
            
            setUpdatedContainer(containerData);
            setShowSuccessModal(true);
            resetForm();
            getContainers(companyId, options);
            setLoading(false);
          },
          () => {
            setLoading(false);
          }
        );
      }
    },
    [companyId, container, options, putContainer, resetForm, getContainers, containerType, containerTypes, containerCategories, locationOptions]
  );

  const filteredContainerCategories = useMemo(() => {
    return (containerCategories as ContainerCategory[]).filter(
      (category) =>
        category.container_type_id === containerType
    );
  }, [containerCategories, containerType]);

  const onDelete = useCallback(() => {
    Swal.fire({
      title: 'Confimar acción',
      text: `Eliminar el contenedor ${container?.description}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si, eliminar!',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed && companyId && container) {
        setLoading(true);

        deleteContainer(
          companyId,
          container.id,
          () => {
            setLoading(false);
            getContainers(companyId, options);
            resetForm();
          },
          () => {
            setLoading(false);
          }
        );
      }
    });
  }, [
    companyId,
    container,
    options,
    deleteContainer,
    getContainers,
    resetForm,
  ]);

  return (
    <>
      <Dialog
        classes={{
          paper: 'm-8 sm:m-24 min-w-[350px] md:min-w-[700px]',
        }}
        onClose={resetForm}
        open={Array.isArray(dialogs) && dialogs.includes(UPDATE_CONTAINER_DIALOG)}
      >
        <DialogContent>
          <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
            <div className="flex flex-col">
                           <div className="mb-16 flex flex-col">
                 <Typography className="text-2xl font-bold truncate">
                   Modificar Contenedor
                 </Typography>
                 {container?.location_id && (
                   <Typography 
                     variant="body2" 
                     color="text.secondary"
                     sx={{ mt: 1, fontStyle: 'italic' }}
                   >
                     Ubicación actual: {locations.find(loc => loc.id === container.location_id)?.code || 'No encontrada'}
                   </Typography>
                 )}
               </div>

              <div className="w-full max-w-3xl mt-20">
                <div className="flex flex-col space-y-32">
                  <form noValidate onSubmit={handleSubmit(onSubmit)}>
                    <div className="mb-24">
                      <div className="flex items-center gap-2">
                        <Controller
                          name="code"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Código del Contenedor"
                              variant="outlined"
                              disabled={true}
                              error={!!errors.code}
                              helperText={errors?.code?.message}
                              fullWidth
                            />
                          )}
                        />
                        {container?.code && (
                          <CopyButton
                            textToCopy={container.code}
                            tooltip="Copiar código del contenedor"
                          />
                        )}
                      </div>
                    </div>

                    <div className="mb-24">
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Descripción"
                            variant="outlined"
                            disabled={loading}
                            error={!!errors.description}
                            helperText={errors?.description?.message}
                            fullWidth
                          />
                        )}
                      />
                    </div>

                    <div className="mb-24">
                      <Controller
                        name="container_status_id"
                        control={control}
                        render={({ field }) => (
                          <FormControl variant="outlined" fullWidth>
                            <InputLabel id="container-status-label" required>
                              Estado
                            </InputLabel>
                            <Select
                              {...field}
                              labelId="container-status-label"
                              label="Estado"
                              disabled={loading}
                              required
                            >
                              {DESTINY_CONTAINER_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                  {type.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </div>

                    <div className="mb-24">
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel id="container-type-label" required>
                          Tipo de Contenedor
                        </InputLabel>
                        <Select
                          labelId="container-type-label"
                          label="Tipo de Contenedor"
                          value={`${containerType}`}
                          disabled={true}
                          required
                        >
                          {containerTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                              {type.description || type.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>

                    <div className="mb-24">
                      <Controller
                        name="container_category_id"
                        control={control}
                        render={({ field }) => (
                          <ContainerCategorySelect
                            value={field.value || undefined}
                            onChange={(newValue) => {
                              field.onChange(newValue === 'all' ? undefined : newValue);
                            }}
                            categories={filteredContainerCategories as ContainerCategory[]}
                            label="Categoría"
                            required
                            disabled={true}
                            size="medium"
                          />
                        )}
                      />
                    </div>

                    

                    <div className="mb-24">
                      <Controller
                        name="container_recipient_type_id"
                        control={control}
                        render={({ field }) => (
                          <FormControl variant="outlined" fullWidth>
                            <InputLabel id="recipient-type-label" required>
                              Tipo de Recipiente
                            </InputLabel>
                            <Select
                              {...field}
                              labelId="recipient-type-label"
                              label="Tipo de Recipiente"
                              disabled={loading}
                              required
                              error={!!errors.container_recipient_type_id}
                            >
                              {containerRecipientTypes.map((type) => (
                                <MenuItem key={type.id} value={type.id}>
                                  {type.description || type.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.container_recipient_type_id && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                {errors.container_recipient_type_id.message}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                      />
                    </div>

                    <div className="mb-24">
                      <Controller
                        name="price_range_id"
                        control={control}
                        render={({ field }) => (
                          <FormControl variant="outlined" fullWidth>
                            <InputLabel id="range-label">
                              Rango de Precios
                            </InputLabel>
                            <Select
                              labelId="range-label"
                              label="Rango de Precios"
                              disabled={loading}
                              value={field.value ?? ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                field.onChange(value === '' ? null : Number(value));
                              }}
                              onBlur={field.onBlur}
                              name={field.name}
                            >
                              <MenuItem value="">
                                <em>Sin rango</em>
                              </MenuItem>
                              {priceRanges.map((priceRange) => (
                                <MenuItem
                                  key={priceRange.id}
                                  value={priceRange.id}
                                >
                                  {priceRange.description}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </div>

                    <div className="mb-24">
                      <Controller
                        name="channel_type_id"
                        control={control}
                        render={({ field }) => (
                          <FormControl variant="outlined" fullWidth>
                            <InputLabel id="channel-label">Canal</InputLabel>
                            <Select
                              labelId="channel-label"
                              label="Canal"
                              disabled={loading}
                              value={field.value ?? ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                field.onChange(value === '' ? null : Number(value));
                              }}
                              onBlur={field.onBlur}
                              name={field.name}
                            >
                              <MenuItem value="">
                                <em>Sin canal</em>
                              </MenuItem>
                              {channelTypes.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                  {category.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </div>

                    <div className="mb-24">
                      <Controller
                        name="location_id"
                        control={control}
                        render={({ field }) => (
                          <FormControl variant="outlined" fullWidth>
                            <Autocomplete
                              options={locationOptions}
                              getOptionLabel={(option) => option.label}
                              value={locationOptions.find(loc => loc.id === field.value) || null}
                              onChange={(event, newValue) => {
                                field.onChange(newValue?.id || '');
                              }}
                              loading={locations.length === 0}
                              filterOptions={(options, { inputValue }) => {
                                const searchTerm = inputValue.toLowerCase();
                                return options.filter(option => 
                                  option.code.toLowerCase().includes(searchTerm) ||
                                  option.label.toLowerCase().includes(searchTerm) ||
                                  option.warehouse_code.toLowerCase().includes(searchTerm)
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Cambiar Ubicación"
                                  placeholder="Buscar por código o bodega..."
                                  error={!!errors.location_id}
                                  helperText={errors?.location_id?.message}
                                  InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                      <Box sx={{ mr: 1 }}>
                                        <SearchOutlinedIcon sx={{ color: 'text.secondary' }} />
                                      </Box>
                                    ),
                                    endAdornment: (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {params.InputProps.endAdornment}
                                      </Box>
                                    ),
                                  }}
                                  disabled={loading}
                                />
                              )}
                              renderOption={(props, option) => (
                                <li {...props}>
                                  <Box>
                                    <Typography variant="body2" fontWeight="normal">
                                      {option.label}
                                    </Typography>
                                  </Box>
                                </li>
                              )}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: 'grey.300',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: 'primary.main',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main',
                                  },
                                },
                              }}
                            />
                          </FormControl>
                        )}
                      />
                    </div>

                    {/* Sección de Imágenes (Opcional) */}
                    <div className="mb-24">
                      <Typography variant="subtitle1" className="mb-16 font-semibold">
                        Imágenes del Contenedor (Opcional)
                      </Typography>
                      <Typography variant="caption" color="text.secondary" className="mb-16 block">
                        Máximo {MAX_CONTAINER_FILES} imágenes
                      </Typography>
                      
                      {/* Mostrar imágenes existentes */}
                      {container?.files && container.files.length > 0 && (
                        <div className="mb-16">
                          <Typography variant="body2" className="mb-8 font-medium">
                            Imágenes existentes:
                          </Typography>
                          <div className="flex flex-wrap gap-12">
                            {container.files
                              .filter(file => !filesToDelete.includes(file.id))
                              .map((file) => (
                                <div
                                  key={file.id}
                                  className="relative w-[100px] h-[100px] rounded shadow overflow-hidden"
                                >
                                  <img
                                    src={file.url}
                                    alt={`Imagen ${file.id}`}
                                    className="object-cover w-full h-full"
                                  />
                                  <IconButton
                                    color="error"
                                    size="small"
                                    aria-label="delete picture"
                                    className="absolute top-0 right-0"
                                    onClick={() => {
                                      setFilesToDelete([...filesToDelete, file.id]);
                                    }}
                                    sx={{
                                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                      '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                      },
                                    }}
                                  >
                                    <HighlightOffIcon fontSize="small" />
                                  </IconButton>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* Calcular espacios disponibles */}
                      {(() => {
                        const existingFiles = container?.files?.filter(f => !filesToDelete.includes(f.id)).length || 0;
                        const totalFiles = existingFiles + newFiles.length;
                        const canAddMore = totalFiles < MAX_CONTAINER_FILES;
                        const remainingSlots = MAX_CONTAINER_FILES - totalFiles;

                        return (
                          <>
                            <div className="mb-16">
                              <label htmlFor="container-image-input-edit">
                                <Button
                                  color="primary"
                                  variant="outlined"
                                  aria-label="upload picture"
                                  component="span"
                                  disabled={loading || !canAddMore}
                                  startIcon={<PhotoCamera />}
                                >
                                  Agregar Imágenes {remainingSlots > 0 && `(${remainingSlots} disponibles)`}
                                </Button>
                              </label>
                              <input
                                id="container-image-input-edit"
                                type="file"
                                accept="image/*"
                                className="hidden"
                                multiple
                                onChange={(e) => {
                                  if (e.target.files) {
                                    const files = Array.from(e.target.files);
                                    const filesToAdd = files.slice(0, remainingSlots);
                                    setNewFiles([...newFiles, ...filesToAdd]);
                                    // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
                                    e.target.value = '';
                                  }
                                }}
                              />
                            </div>

                            {newFiles.length > 0 && (
                              <div className="mb-16">
                                <Typography variant="body2" className="mb-8 font-medium">
                                  Nuevas imágenes a subir:
                                </Typography>
                                <div className="flex flex-wrap gap-12">
                                  {newFiles.map((file, index) => (
                                    <div
                                      key={index}
                                      className="relative w-[100px] h-[100px] rounded shadow overflow-hidden"
                                    >
                                      <img
                                        src={URL.createObjectURL(file)}
                                        alt={`Preview ${index + 1}`}
                                        className="object-cover w-full h-full"
                                      />
                                      <IconButton
                                        color="error"
                                        size="small"
                                        aria-label="delete picture"
                                        className="absolute top-0 right-0"
                                        onClick={() => {
                                          const updatedFiles = newFiles.filter((_, i) => i !== index);
                                          setNewFiles(updatedFiles);
                                        }}
                                        sx={{
                                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                          '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                          },
                                        }}
                                      >
                                        <HighlightOffIcon fontSize="small" />
                                      </IconButton>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>

                    {canWrite && (
                      <div className="flex justify-center gap-4">
                        <Button
                          variant="contained"
                          color="error"
                          sx={{ borderRadius: 50 }}
                          disabled={loading}
                          type="button"
                          onClick={onDelete}
                        >
                          <Spinner show={loading} />
                          Eliminar
                        </Button>
                        <Button
                          variant="contained"
                          color="secondary"
                          sx={{ borderRadius: 50 }}
                          disabled={!isEmpty(errors) || !isValid || loading}
                          type="submit"
                          size="large"
                        >
                          <Spinner show={loading} />
                          Guardar
                        </Button>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de éxito */}
      <Dialog
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            minHeight: 400,
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'success.main' }}>
                ✓
              </Avatar>
              <Typography variant="h5" color="success.main" fontWeight="bold">
                ¡Contenedor Actualizado Exitosamente!
              </Typography>
            </Box>
            <Button 
              onClick={() => setShowSuccessModal(false)}
              size="small"
              color="error"
              variant="text"
            >
              ✕
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent>
          {updatedContainer && (
            <Box display="flex" flexDirection="column" gap={3}>
              {/* Información principal del contenedor */}
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Grid container spacing={3}>
                  {/* Icono del contenedor */}
                  <Grid item xs={12} md={4}>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                      <Avatar
                        sx={{
                          width: 120,
                          height: 120,
                          bgcolor: 'primary.main',
                          fontSize: '3rem'
                        }}
                      >
                        📦
                      </Avatar>
                    </Box>
                  </Grid>

                  {/* Detalles del contenedor */}
                  <Grid item xs={12} md={8}>
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {updatedContainer.code}
                      </Typography>
                      
                      {updatedContainer.description && (
                        <Typography variant="body1" color="text.secondary">
                          <strong>Descripción:</strong> {updatedContainer.description}
                        </Typography>
                      )}
                      
                      <Typography variant="body2" color="text.secondary">
                        <strong>Tipo:</strong> {updatedContainer.container_type_name}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        <strong>Categoría:</strong> {updatedContainer.container_category_name}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        <strong>Estado:</strong> {updatedContainer.container_status_name}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        <strong>Ubicación:</strong> {updatedContainer.location_description}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Código del contenedor */}
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Código del Contenedor
                </Typography>
                
                <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="body2" color="text.secondary">
                      Código: {updatedContainer.code}
                    </Typography>
                    <CopyButton
                      textToCopy={updatedContainer.code || ''}
                      tooltip="Copiar código"
                    />
                  </Box>
                  
                  {/* Botón de imprimir etiqueta */}
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<PrintIcon />}
                    onClick={() => {
                      // Crear un objeto Container básico para la impresión
                      const containerForPrint: Container = {
                        id: updatedContainer.id,
                        code: updatedContainer.code,
                        description: updatedContainer.description,
                        container_status_id: updatedContainer.container_status_id,
                        container_type_id: containerType || 0,
                        container_category_id: 0,
                        channel_type_id: 0,
                        price_range_id: 0,
                        location_id: 0,
                      };
                      printDestinationContainer(containerForPrint);
                    }}
                    sx={{ mt: 2 }}
                  >
                    Imprimir Etiqueta
                  </Button>
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setShowSuccessModal(false)}
            size="large"
          >
            Cerrar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              setShowSuccessModal(false);
              // Redirigir al listado de contenedores
              window.location.href = '/dashboard/containers';
            }}
            size="large"
          >
            Ir al Listado
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UpdateDialog;
